import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { CreditCard, Crown, Shield, Calendar } from "lucide-react";
import { openExternalUrl } from "@/utils/nativePlugins";

interface SubscriptionCardProps {
  showUpgradeOptions?: boolean;
  userType: 'buyer' | 'seller';
}

// Product mapping for display - LIVE MODE
const PRODUCT_INFO = {
  // Buyer products - LIVE
  'prod_TDc4VzZ2qshOsN': { name: 'Investor Access Monthly', price: '$9.99/month', color: 'bg-blue-500' },
  'prod_TDdbzZM8mwxupk': { name: 'Investor Access Yearly', price: '$99.99/year', color: 'bg-blue-600' },
  
  // Seller products - LIVE
  'prod_TDc4H4sIqTlOJM': { name: 'Shark Plan Monthly', price: '$34.99/month', color: 'bg-yellow-500' },
  'prod_TDc4LIUHwCkuea': { name: 'Shark Plan Yearly', price: '$349.99/year', color: 'bg-yellow-600' },
  'prod_VSd9RAkBproawz': { name: 'Shark Plan Monthly', price: '$34.99/month', color: 'bg-yellow-500' },
  'prod_VSdACnJTrTZ9Zo': { name: 'Shark Plan Yearly', price: '$349.99/year', color: 'bg-yellow-600' },
};


const SubscriptionCard = ({ showUpgradeOptions = true, userType }: SubscriptionCardProps) => {
  const navigate = useNavigate();
  const { 
    subscribed, 
    subscriptionStatus, 
    productId, 
    subscriptionEnd,
    isLoading,
    createCheckoutSession,
    openCustomerPortal
  } = useSubscription();

  const currentProduct = productId ? PRODUCT_INFO[productId as keyof typeof PRODUCT_INFO] : null;
  

  const handleManageSubscription = async () => {
    try {
      const url = await openCustomerPortal();
      await openExternalUrl(url);
    } catch (error: any) {
      toast({
        title: "Error", 
        description: error.message || "Failed to open subscription management",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = () => {
    const badgeClass = "inline-flex items-center gap-1";
    
    switch (subscriptionStatus) {
      case 'active':
        return <Badge className={`${badgeClass} bg-green-500`}><Shield className="w-3 h-3" />Active</Badge>;
      case 'trial':
        return <Badge className={`${badgeClass} bg-blue-500`}><Calendar className="w-3 h-3" />Free Trial</Badge>;
      case 'sponsored':
        return <Badge className={`${badgeClass} bg-purple-500`}><Crown className="w-3 h-3" />Sponsored Access</Badge>;
      case 'expired':
      case 'cancelled':
        return <Badge className={`${badgeClass} bg-red-500`}><Shield className="w-3 h-3" />Expired</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Current Subscription Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Current Plan
              </CardTitle>
              <CardDescription>
                {subscriptionStatus === 'sponsored' 
                  ? 'Admin-granted sponsored access'
                  : (currentProduct?.name || 'No active subscription')
                }
              </CardDescription>
            </div>
            {getStatusBadge()}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">
                {subscriptionStatus === 'sponsored' 
                  ? 'Complimentary' 
                  : (currentProduct?.price || 'No active plan')
                }
              </p>
              {subscriptionEnd && subscriptionStatus !== 'sponsored' && (
                <p className="text-sm text-muted-foreground">
                  {subscriptionStatus === 'trial' ? 'Trial ends' : 'Renews'} on {format(new Date(subscriptionEnd), 'PPP')}
                </p>
              )}
              {subscriptionStatus === 'sponsored' && (
                <p className="text-sm text-muted-foreground">
                  Permanent access granted by administrator
                </p>
              )}
            </div>
            {subscriptionStatus !== 'sponsored' && (
              <Button 
                variant="outline" 
                onClick={() => navigate(userType === 'buyer' ? '/buyer/manage-subscription' : '/seller/manage-subscription')}
              >
                Manage Subscription
              </Button>
            )}
          </div>
          
          {/* Trial expiration warning */}
          {subscriptionStatus === 'trial' && subscriptionEnd && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-700">
                Your free trial expires in {Math.ceil((new Date(subscriptionEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24))} days.
                Upgrade now to continue accessing all features.
              </p>
            </div>
          )}

          {/* Simple Upgrade CTA */}
          {showUpgradeOptions && (subscriptionStatus === 'trial' || subscriptionStatus === 'expired') && (
            <div className="pt-4 border-t mt-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium">Ready to upgrade?</p>
                  <p className="text-sm text-muted-foreground">
                    Choose your plan and complete payment to continue accessing all features.
                  </p>
                </div>
                <Button
                  onClick={() => navigate(userType === 'buyer' ? '/buyer/upgrade' : '/seller/upgrade')}
                  className="bg-[#ffd700] text-[#080c14] hover:bg-[#e6c200] shrink-0"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade Now
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionCard;